/*
 * sessionStore — persists practice sessions across visits (localStorage), so a
 * learner can close the app and CONTINUE where they left off. `current` is the
 * active session (full plan); `history` keeps the most recent sessions
 * (slimmed) so the UI can list them and show their details.
 * Every update rewrites `updatedAt`, so "current" always reflects the latest
 * state of the session (mode switches, revised plans, new options).
 */
const CUR_KEY = 'nano_session_v1';
const HIST_KEY = 'nano_session_hist_v1';
const HIST_MAX = 12;

const read = (k, fb) => {
  try {
    const v = JSON.parse(localStorage.getItem(k));
    return v === null || v === undefined ? fb : v;
  } catch { return fb; }
};
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* quota/private mode */ } };

// History entries drop the bulky progress table but keep what details need.
const slim = (s) => ({
  id: s.id, subjects: s.subjects, includedModes: s.includedModes,
  options: s.options, mode: s.mode, status: s.status,
  startedAt: s.startedAt, updatedAt: s.updatedAt,
  plan: s.plan ? {
    rationale: s.plan.rationale, focus_areas: s.plan.focus_areas,
    allocation: s.plan.allocation, cold_start: s.plan.cold_start,
    generated_by: s.plan.generated_by,
  } : null,
});

const upsertHistory = (s) => {
  const h = read(HIST_KEY, []).filter((x) => x && x.id !== s.id);
  h.unshift(slim(s));
  write(HIST_KEY, h.slice(0, HIST_MAX));
};

/** Save (or update) the active session; returns the stored record. */
export function save(session) {
  const now = new Date().toISOString();
  const cur = {
    id: session.id || `s_${Date.now().toString(36)}`,
    startedAt: session.startedAt || now,
    ...session,
    updatedAt: now,
  };
  write(CUR_KEY, cur);
  upsertHistory(cur);
  return cur;
}

/** Patch the active session (e.g. the learner switched mode). No-op if none. */
export function touch(patch) {
  const c = read(CUR_KEY, null);
  return c ? save({ ...c, ...patch }) : null;
}

export function current() { return read(CUR_KEY, null); }

export function clearCurrent() { try { localStorage.removeItem(CUR_KEY); } catch { /* ignore */ } }

/** Most-recent-first list of sessions (slimmed; includes the active one). */
export function history() { return read(HIST_KEY, []); }

// ---- Session-Settings draft (topic selection + options survive tab switches)
const DRAFT_KEY = 'nano_settings_draft_v1';
export function saveDraft(draft) { write(DRAFT_KEY, draft); }
export function loadDraft() { return read(DRAFT_KEY, null); }
export function clearDraft() { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } }

// ---- Last interview / rapidfire debrief (so #/report survives a refresh).
// Stored verbatim (metrics, question_evaluations, concept_performance,
// recommendations, rapidfire, overall_assessment) — the report screen reads
// the raw backend object. Expires after a week; the scores live on in Memory.
const REPORT_KEY = 'nano_last_report_v1';
export function saveReport(report) {
  if (!report) return;
  write(REPORT_KEY, { report, savedAt: Date.now() });
}
export function lastReport(maxAgeMs = 7 * 24 * 3600e3) {
  const r = read(REPORT_KEY, null);
  return r && r.report && typeof r.savedAt === 'number' && Date.now() - r.savedAt < maxAgeMs ? r.report : null;
}
export function clearReport() { try { localStorage.removeItem(REPORT_KEY); } catch { /* ignore */ } }
